import { IFormContext, IFormDefinition, IFormSingleResponse, isFunction, isPromiseLike } from '@ngt/opms';
import { useMemo } from 'react';
import { KeyedMutator, MutatorCallback } from 'swr';
import { GetConsentEventDataResponse, IEConsentForm } from '../../api/dtos';
import useConsentEventData from '../data/useConsentEventData';



const useGenerateEConsentFormContext = (formDefinition: IFormDefinition | undefined, consentEventContext: ReturnType<typeof useConsentEventData>) => {
    
    const formContext: IFormContext<IEConsentForm> = useMemo(() => {
        const form = consentEventContext.data?.consentEventEConsentForms.find(x => x.formDefinition?.code === formDefinition?.code);
        const error = consentEventContext.error;

        const formMutate: KeyedMutator<IFormSingleResponse<IEConsentForm>> = async (data, opts) => {
            let newData: GetConsentEventDataResponse = { ...(consentEventContext.data as any), consentEventEConsentForms: [...(consentEventContext.data?.consentEventEConsentForms ?? [])], responseStatus: error as any };
            let newSingleData: IFormSingleResponse<IEConsentForm> | undefined = undefined;
            
            if (!data) {
                newSingleData = undefined;
            }
            else if (isFunction(data)) {
                const existingSingleData = { form, responseStatus: error } as IFormSingleResponse<IEConsentForm>;

                const callback = (data as MutatorCallback<IFormSingleResponse<IEConsentForm>>)(existingSingleData);
                
                if (isPromiseLike(callback)) {
                    newSingleData = await (callback as Promise<IFormSingleResponse<IEConsentForm>>);
                }
                else {
                    newSingleData = callback;
                }
            }
            else if (isPromiseLike(data)) {
                newSingleData = await (data as Promise<IFormSingleResponse<IEConsentForm>>);
            }

            if (!newSingleData) {
                newData.consentEventEConsentForms = newData.consentEventEConsentForms?.filter(x =>  x.formDefinition?.code !== formDefinition?.code);
            }
            else {
                if (newSingleData.responseStatus) {
                    newData.responseStatus = newSingleData.responseStatus;
                }

                if (newData.consentEventEConsentForms) {
                    if (newSingleData.form) {
                        const index = newData.consentEventEConsentForms.findIndex(x => x.formDefinition?.code === formDefinition?.code);
        
                        if (index !== -1) {
                            newData.consentEventEConsentForms[index] = newSingleData.form;
                        }
                        else {
                            newData.consentEventEConsentForms.push(newSingleData.form)
                        }
                    }
                    else {
                        newData.consentEventEConsentForms = newData.consentEventEConsentForms.filter(x => x.formDefinition?.code !== formDefinition?.code);
                    }
                }
                else {
                    if (newSingleData.form) {
                        newData.consentEventEConsentForms = [newSingleData.form];
                    }
                }
            }

            const updatedData = await consentEventContext.mutate(newData, opts as any);

            if (updatedData) {
                return {
                    form: updatedData.consentEventEConsentForms?.find(x => x.formDefinition?.code === formDefinition?.code),
                    responseStatus: updatedData.responseStatus
                } as IFormSingleResponse<IEConsentForm>
            }

            return undefined;
        }

        const formRefresh = async () => {
            const newData = await consentEventContext.refresh();

            return newData?.consentEventEConsentForms?.find(x => x.formDefinition?.code === formDefinition?.code);
        }

        return {
            data: form,
            error: consentEventContext.error,
            loading: consentEventContext.loading,
            validating: consentEventContext.validating,
            mutate: formMutate,
            refresh: formRefresh
        }
    }, [consentEventContext, formDefinition])

    return formContext
}

export default useGenerateEConsentFormContext;